import { Modal } from "@msys/ui";
import {
  Alert,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useOpenFile } from "../../commons/hooks/useOpenFile.js";
import { useDownloadQuotePdf } from "../quotes/hooks/useDownloadQuotePdf.js";
import {
  GenerateQuotePdfModal_QuoteFragment,
  useGenerateQuotePdfModal_PdfLetterheadsQuery,
} from "./GenerateQuotePdfModal.generated.js";
import {
  useQuotePdfGenerationOptions,
  type QuotePdfGenerationOption,
} from "./useQuotePdfGenerationOptions.js";

const NAME_MAX = 255;

// The picker's value for "no letterhead". Never sent: showing it means nothing
// was picked, so the template's own "none" setting is what the server resolves.
const NO_LETTERHEAD = "";

export function GenerateQuotePdfModal({
  projectId,
  docId,
  doc,
  canSave,
  handleClose,
}: {
  projectId: string;
  docId: string;
  doc: GenerateQuotePdfModal_QuoteFragment;
  canSave: boolean;
  handleClose: () => void;
}) {
  const { t } = useTranslate(["Quote", "Global"]);
  const { openPdf } = useOpenFile();
  const { options, loading } = useQuotePdfGenerationOptions({ doc });
  const { handleDownload, handleSaveAndShare, loadingPdf } =
    useDownloadQuotePdf({ projectId, docId });

  // Revalidated on every open. `PdfLetterheadOption` is a distinct typename
  // from the `PdfLetterhead` the admin mutations write and evict, so nothing
  // those mutations do to the cache reaches these rows — cache-first would keep
  // offering a deleted letterhead, or preselect a default that has since moved.
  const { data: letterheadData, loading: loadingLetterheads } =
    useGenerateQuotePdfModal_PdfLetterheadsQuery({
      fetchPolicy: "cache-and-network",
    });
  const viewer =
    letterheadData?.viewer?.__typename === "Viewer"
      ? letterheadData.viewer
      : null;
  const letterheads = viewer?.organisation.pdfLetterheads ?? [];
  const defaultLetterheadId =
    letterheads.find(letterhead => letterhead.isDefault)?.id ?? null;
  const [selectedKey, setSelectedKey] = React.useState<string>("");
  // Null means "no pick": the server resolves the template's setting, else the
  // organisation's default. Keeping it null rather than seeding an id is what makes the reset in D15 automatic: this
  // component is mounted fresh each time the modal opens, so there is no state
  // that could carry a previous choice over.
  const [selectedLetterheadId, setSelectedLetterheadId] = React.useState<
    string | null
  >(null);
  const [save, setSave] = React.useState(false);
  // Null until the user types: the name follows the selected template's label
  // for as long as they leave it alone.
  const [editedName, setEditedName] = React.useState<string | null>(null);

  const selected =
    options.find(option => option.key === selectedKey) ?? options[0];
  const name = editedName ?? selected?.label ?? "";
  const trimmed = name.trim();
  const isNameValid = trimmed.length > 0 && trimmed.length <= NAME_MAX;

  // A generator template holding no assignment composes no letterhead
  // (MEIS-13565), where every other option falls to the default.
  const templateComposesNone = selected?.letterheadSetting === "NONE";

  // Only offered when there is a choice to make: between letterheads, or — for
  // a template that composes none — between none and the one there is. Whether
  // the viewer may generate at all is settled before this component exists: the
  // sole mount site renders it behind `isOwner`, and `QuoteDocumentService`
  // refuses a non-owner outright, so these letterheads always belong to the
  // quote's own organisation — the one generation resolves the pick against.
  const showLetterheadPicker =
    letterheads.length > 1 || (letterheads.length > 0 && templateComposesNone);

  // What the picker shows, in the server's own precedence order: the user's
  // pick, else the selected template's setting (its letterhead, none, or the
  // default), else the default. What is sent stays `selectedLetterheadId`, so
  // an untouched picker sends nothing and the server resolves the levels at
  // generation time — otherwise a default or an assignment moved while this
  // modal was open would be overridden by the stale one shown.
  const templateLetterheadId = templateComposesNone
    ? NO_LETTERHEAD
    : (selected?.pdfLetterheadId ?? defaultLetterheadId);
  const displayedLetterheadId = selectedLetterheadId ?? templateLetterheadId;

  const isBusy = Boolean(loadingPdf);
  const canSubmit = Boolean(selected) && (!save || isNameValid) && !isBusy;

  // Each group's label is rendered once, above its first option.
  const renderedGroups = new Set<string>();

  return (
    <Modal
      title={t("Generate PDF", { ns: "Quote" })}
      handleClose={handleClose}
      maxWidth="xs"
      isLoading={loading || loadingLetterheads}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text", disabled: isBusy },
        },
        {
          label: t("Generate", { ns: "Global" }),
          handleClick: async () => {
            if (!selected || !canSubmit) return;
            const url = save
              ? await handleSaveAndShare(
                  selected.args,
                  trimmed,
                  selectedLetterheadId
                )
              : await handleDownload(selected.args, selectedLetterheadId);
            if (!url) return;
            handleClose();
            await openPdf(url, `Quote_${docId}`);
          },
          buttonProps: { variant: "contained", disabled: !canSubmit },
        },
      ]}
    >
      <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
        <TextField
          variant="outlined"
          select
          label={t("Template", { ns: "Quote" })}
          value={selected?.key ?? ""}
          onChange={event => {
            const next = options.find(
              option => option.key === event.target.value
            );
            setSelectedKey(event.target.value);
            setEditedName(null);
            // A pick was made about the previous template. Clearing it lets the
            // new template's own assignment show through, rather than carrying
            // over a choice that was never about this template. Only on an
            // actual template change: the agreed and proposed options of one
            // generator template share its assignment, so moving between them
            // is not one, and losing the pick there would be unexplainable.
            if (assignedTemplateIdOf(next) !== assignedTemplateIdOf(selected)) {
              setSelectedLetterheadId(null);
            }
          }}
          disabled={isBusy}
          fullWidth
          size="small"
        >
          {options.flatMap(option => {
            const items = [];
            if (!renderedGroups.has(option.group)) {
              renderedGroups.add(option.group);
              items.push(
                <ListSubheader key={`group-${option.group}`}>
                  {option.group}
                </ListSubheader>
              );
            }
            items.push(
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            );
            return items;
          })}
        </TextField>

        {showLetterheadPicker && (
          <TextField
            variant="outlined"
            select
            label={t("Letterhead", { ns: "Global" })}
            value={displayedLetterheadId ?? NO_LETTERHEAD}
            // Choosing what the template already resolves to is "no pick", so
            // the server keeps resolving it: sending the shown id would pin an
            // assignment or default that may move while the modal is open.
            onChange={event =>
              setSelectedLetterheadId(
                event.target.value === templateLetterheadId
                  ? null
                  : event.target.value
              )
            }
            disabled={isBusy}
            fullWidth
            size="small"
            // "No letterhead" is the empty value, which a select otherwise
            // renders as a blank field under an unshrunk label.
            slotProps={{
              select: { displayEmpty: true },
              inputLabel: { shrink: true },
            }}
          >
            {/* Only as the template's own setting, never as a general opt-out:
                a template that composes a letterhead keeps composing one. */}
            {templateComposesNone && (
              <MenuItem value={NO_LETTERHEAD}>
                {t("No letterhead", { ns: "Quote" })}
              </MenuItem>
            )}
            {letterheads.map(letterhead => (
              <MenuItem key={letterhead.id} value={letterhead.id}>
                {letterhead.name}
              </MenuItem>
            ))}
          </TextField>
        )}

        {canSave && (
          <FormControlLabel
            control={
              <Switch
                checked={save}
                onChange={event => setSave(event.target.checked)}
                disabled={isBusy}
              />
            }
            label={t("Save this document", { ns: "Quote" })}
          />
        )}

        {save && (
          <>
            <Alert severity="info">
              {t(
                "Saved documents are visible to every organisation with read access to this quote.",
                { ns: "Quote" }
              )}
            </Alert>
            <TextField
              variant="outlined"
              label={t("Document name", { ns: "Quote" })}
              value={name}
              onChange={event => setEditedName(event.target.value)}
              slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
              error={name.length > 0 && !isNameValid}
              disabled={isBusy}
              fullWidth
              size="small"
            />
          </>
        )}
      </Stack>
    </Modal>
  );
}

/** The template whose assignment an option would show, if it names one at all. */
function assignedTemplateIdOf(
  option: QuotePdfGenerationOption | undefined
): string | null {
  return option?.args.templateType === "generator"
    ? option.args.pdfGeneratorTemplateId
    : null;
}
